import { useEffect, useState } from "react";

import { Close, IbmWatsonStudio, Menu } from "@carbon/icons-react";
import { NavLink } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LIBRARY_URL } from "@/config/api";
import CreateGroupModal from "@/features/groups/components/CreateGroupModal/CreateGroupModal";
import { industryGroups, myGroupsLinks, personalLinks } from "../data";

const Nav = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        isMobileMenuOpen &&
        !target.closest(".mobile-nav") &&
        !target.closest(".mobile-menu-toggle")
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      <button
        className="mobile-menu-toggle fixed top-[85px] left-4 z-30 lg:hidden bg-white border border-gray-200 rounded-lg p-2 shadow-lg hover:bg-gray-50 transition-colors"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle navigation menu"
      >
        {isMobileMenuOpen ? (
          <Close
            size={20}
            className="text-gray-700"
          />
        ) : (
          <Menu
            size={20}
            className="text-gray-700"
          />
        )}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Navigation Sidebar */}
      <div
        className={`
        mobile-nav
        fixed  
        top-[70px] left-0
        h-[calc(100vh-70px)]
        w-[280px]
        bg-white
 
        z-30
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0
        overflow-y-auto
      `}
      >
        <div className="w-full flex flex-col h-full bg-white">
          {/* Quick Actions */}
          <div className="p-4 py-6 border-b border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Hành động nhanh
            </h3>
            <div className="space-y-3">
              <CreateGroupModal
                isFullWidth
                haveSubGroup={true}
              />
              <NavLink
                to={`${LIBRARY_URL}/studio`}
                className="block w-full"
                target="_blank"
              >
                <Button
                  variant="ghost"
                  size="lg"
                  className="w-full "
                >
                  <IbmWatsonStudio
                    size={16}
                    className="mr-2"
                  />
                  Taga studio
                </Button>
              </NavLink>
            </div>
          </div>

          {/* My Groups */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Nhóm của tôi
            </h3>
            <div className="space-y-1">
              {myGroupsLinks.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <NavLink
                    key={index}
                    to={item.url}
                    onClick={handleLinkClick}
                    className={({ isActive, isPending }) =>
                      `flex items-center justify-between py-2.5 px-3 rounded-lg transition-all duration-200 ${
                        isPending
                          ? "bg-gray-100"
                          : isActive
                          ? "bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 border-l-4 border-l-blue-500 font-semibold"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`
                    }
                  >
                    <div className="flex items-center">
                      <IconComponent
                        className="text-base mr-3"
                        size={18}
                      />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {item.badge && (
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-600 text-xs h-5 min-w-5 flex items-center justify-center"
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>

          {/* Personal Section */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Cá nhân
            </h3>
            <div className="space-y-1">
              {personalLinks.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <NavLink
                    key={index}
                    to={item.url}
                    end
                    onClick={handleLinkClick}
                    className={({ isActive, isPending }) =>
                      `flex items-center py-2.5 px-3 rounded-lg transition-all duration-200 ${
                        isPending
                          ? "bg-gray-100"
                          : isActive
                          ? "bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 border-l-4 border-l-blue-500 font-semibold"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`
                    }
                  >
                    <IconComponent
                      className="text-base mr-3"
                      size={18}
                    />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>

          {/* Popular Industry Groups */}
          <div className="p-4 flex-1">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Nhóm ngành nghề phổ biến
            </h3>
            <div className="space-y-1">
              {industryGroups.map((group, index) => {
                return (
                  <NavLink
                    key={index}
                    to={group.url}
                    end
                    onClick={handleLinkClick}
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg transition-all duration-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{group.emoji}</span>
                      <div>
                        <span className="font-medium text-sm block">
                          {group.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {group.memberCount} thành viên
                        </span>
                      </div>
                    </div>
                    {group.trending && (
                      <Badge
                        variant="secondary"
                        className="bg-orange-100 text-orange-600 text-xs"
                      >
                        🔥
                      </Badge>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>

          {/* Footer CTA */}
          <div className="p-4 border-t border-gray-100 mt-auto">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl border border-blue-100">
              <h4 className="font-semibold text-gray-900 text-sm mb-2">
                🌟 Khám phá thêm
              </h4>
              <p className="text-xs text-gray-600 mb-3">
                Tìm kiếm và tham gia các nhóm chuyên nghiệp mới
              </p>
              <NavLink
                to="/group/discover"
                onClick={handleLinkClick}
              >
                <Button
                  size="sm"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                >
                  Khám phá nhóm
                </Button>
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default Nav;
